import { useCallback, useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// HIDDEN (FM-2406): the top navbar is gone. Its only surviving control, the avatar,
// moved into the page-head row as `components/header/AccountMenu` — rendered by each
// page beside its "Ask Risa" trigger. Header.jsx is untouched; uncomment this import
// and the <Header /> element below to restore the navbar.
// import Header from '../header/Header';
import Footer from '../footer/Footer';
import { CommandPalette, Spinner } from '../reusable';
import { RisaProvider, RisaPanel, useRisa } from '../modules/risa';
// HIDDEN (FM-2406): useAuth / OIDC_ENABLED — the shell no longer builds header
// identity or owns the logout handler; AccountMenu reads the session itself.
// import { useAuth } from '../../auth/AuthProvider';
// import { OIDC_ENABLED } from '../../auth/oidc/oidcConfig';
// HIDDEN (FM-2406): usePlanBranding — the header no longer renders a plan logo, so
// nothing consumes the branding. Uncomment with the `branding` call below.
import { useLanguage } from '../../hooks';
import { SEARCH_GROUPS } from '../../config/searchCatalog';
import '../../styles/app-layout.css';

// HIDDEN (FM-2406): initialsOf moved to AccountMenu with the avatar it feeds.
// const initialsOf = (first = '', last = '') =>
//   `${first[0] || ''}${last[0] || ''}`.toUpperCase() || '–';

/* The shared application shell: a scrollable work
   area in the middle (the active route renders into <Outlet />), and the
   fixed trust Footer pinned to the bottom. Used as the layout route so the
   shell stays mounted while pages swap.

   The shell owns two global surfaces:
   - the ⌘K CommandPalette (global search), and
   - the R.I.S.A assistant (RisaProvider + a single RisaPanel). One panel
     serves every page; pages open it via useRisa() from their "Ask Risa"
     trigger, and ⌘G toggles it. When open, the main column reflows beside the
     fixed panel (.app-shell__main.is-risa-open). */
function AppShell() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // True while the footer language selector is switching locales — shown as an
  // in-shell loader so the header/footer (the selector itself) stay visible.
  const { switching } = useLanguage();
  const [searchOpen, setSearchOpen] = useState(false);
  const { isOpen: risaOpen, context: risaContext, close: closeRisa, toggle: toggleRisa } = useRisa();

  // HIDDEN (FM-2406): the shell used to derive the header's identity (person, plan
  // name) and the plan-logo branding here, then thread them into <Header />. With the
  // navbar gone, AccountMenu derives what it needs from useAuth() itself. Restore this
  // block together with the <Header /> element below.
  // const account = useMemo(
  //   () => accounts.find((a) => a.accountId === selectedAccountId) || accounts[0],
  //   [accounts, selectedAccountId],
  // );
  // const branding = usePlanBranding(account?.planId, account?.planName);
  // const { person, planName } = useMemo(() => { … }, [participant, account]);

  // HIDDEN (FM-2406): handleLogout / handleSwitchPlan moved to AccountMenu, which owns
  // the dropdown that triggers them.
  // const handleLogout = useCallback(() => { logout(); if (!OIDC_ENABLED) navigate('/login', { replace: true }); }, [logout, navigate]);
  // const handleSwitchPlan = useCallback((accountId) => { selectAccount(accountId); navigate('/dashboard'); }, [selectAccount, navigate]);

  // ⌘K / Ctrl-K toggles search; ⌘G / Ctrl-G toggles Risa. (Esc-to-close lives
  // in each surface.)
  useEffect(() => {
    const onKey = (e) => {
      const k = e.key.toLowerCase();
      if ((e.metaKey || e.ctrlKey) && k === 'k') {
        e.preventDefault();
        setSearchOpen((v) => !v);
      } else if ((e.metaKey || e.ctrlKey) && k === 'g') {
        e.preventDefault();
        toggleRisa();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleRisa]);

  const handleSelect = useCallback(
    (item) => {
      setSearchOpen(false);
      if (item?.path) navigate(item.path);
    },
    [navigate],
  );

  return (
    <div className="app-shell">
      {/* HIDDEN (FM-2406): the top navbar. The avatar it used to hold now renders in
          each page's PageHead action row via <AccountMenu />.
      <Header
        person={person}
        planName={planName}
        accounts={accounts}
        selectedAccountId={selectedAccountId}
        onSwitchPlan={handleSwitchPlan}
        onLogout={handleLogout}
      />
      */}
      <main className={'app-shell__main' + (risaOpen ? ' is-risa-open' : '')}>
        <div className="app-shell__page">
          <Outlet />
        </div>

        {switching && (
          <div className="app-shell__lang-overlay" role="status" aria-live="polite">
            <div className="app-shell__lang-card">
              <Spinner size="lg" label={t('footer.switchingLanguage')} />
              <span className="app-shell__lang-text">{t('footer.switchingLanguage')}</span>
            </div>
          </div>
        )}
      </main>
      <Footer />

      {risaOpen && <RisaPanel onClose={closeRisa} context={risaContext} />}

      <CommandPalette
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onSelect={handleSelect}
        groups={SEARCH_GROUPS}
      />
    </div>
  );
}

function AppLayout() {
  return (
    <RisaProvider>
      <AppShell />
    </RisaProvider>
  );
}

export default AppLayout;
